/* eslint-disable @typescript-eslint/no-unused-vars */
import { makeAutoObservable, reaction } from "mobx";
import { RootStore } from "./RootStore";
import { REQUEST_PLAYER_DATES_LIST_COUNT } from "../utils/constants";
import { CourtId, CourtType } from "../types/types";
import { CourtBookingApi } from "../apis/bookingapi";
import {
  CourtBookingPayload,
  CourtBookingPayment,
  InvitePlayersPayload,
  SpecificBookingSlot,
  SpecificBookingSlotsResponse,
} from "../apis/types";
import { Strings } from "../utils/strings";
import {
  BOOKING_FEES_SERVICE_ID,
  BookingPaymentType,
  days,
  months,
  TOTAL_PLAYERS_IN_MATCH,
  ULTRA_AI_PLAYERS_ID,
  ULTRA_SERVICE_LOCATION_ENUM,
} from "../utils/data";
import { NavigateFunction } from "react-router-dom";
import { getDateInYYYYMMDDFormat } from "../utils/utils";

export interface Court {
  id: number;
  name: string;
  available: boolean;
}

export interface DateInfo {
  date: string;
  day: string;
  month: string;
  fullDate: Date;
}

// Add these properties and methods to your CourtBookingStore class
export class CourtBookingStore {
  rootStore: RootStore;
  isLoading: boolean = false;

  nextSixDays: DateInfo[] = [];
  selectedDate: DateInfo | null = null;
  modalData?: {
    visible: boolean;
    title: string;
    children: React.ReactNode | string;
    btnText?: string;
    btnVariant?: string;
    onClose: () => void;
    onAccept?: () => void;
    headerIcon?: React.ReactNode;
  };

  timeSlotsResponse: SpecificBookingSlotsResponse | null = null;
  timeSlots: SpecificBookingSlot[] = [];
  selectedTimeSlots: SpecificBookingSlot[] = [];
  isMinimumPlayingTimeCovered: boolean = false;
  minimumPlayingTime: number = 0;
  totalPlayersInMatch: number = TOTAL_PLAYERS_IN_MATCH;
  totalPlayersToSelect: number = TOTAL_PLAYERS_IN_MATCH - 1;

  // Reset all store values to their initial state
  resetStore = () => {
    this.isLoading = false;
    this.nextSixDays = [];
    this.selectedDate = null;
    this.modalData = undefined;
    this.timeSlotsResponse = null;
    this.timeSlots = [];
    this.selectedTimeSlots = [];
    this.isMinimumPlayingTimeCovered = false;
    this.minimumPlayingTime = 0;
    this.selectedCourt = this.courts[0];
    this.totalPlayersInMatch = TOTAL_PLAYERS_IN_MATCH;
    this.totalPlayersToSelect = TOTAL_PLAYERS_IN_MATCH - 1;
    // Regenerate the next six days
    this.generateNextSixDays();
  };

  courts: CourtType[] = [
    {
      id: CourtId.Indoor,
      title: "Indoor",
    },
    {
      id: CourtId.Outdoor,
      title: "Outdoor",
    },
    {
      id: CourtId.Singles,
      title: "Singles",
    },
    {
      id: CourtId.IndoorLegacy,
      title: "Indoor Legacy",
    },
  ];
  selectedCourt: CourtType = this.courts[0];

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, {
      rootStore: false,
    });

    reaction(
      () => this.selectedDate,
      (current) => {
        if (current !== null) {
          this.fetchTimeSlots();
        }
      }
    );

    reaction(
      () => this.selectedTimeSlots,
      (current) => {
        if (current !== null) {
          this.setIsMinimumPlayingTimeCovered(
            this.selectedTimeSlots,
            this.minimumPlayingTime
          );
        }
      }
    );

    reaction(
      () => this.totalPlayersInMatch,
      (current) => {
        this.totalPlayersToSelect = this.totalPlayersInMatch - 1;
      }
    );

    reaction(
      () => ({
        court: this.selectedCourt,
        date: this.timeSlotsResponse,
      }),
      (_current) => {
        this.clearSelectedTimeSlots();
        if (this.timeSlotsResponse !== null) {
          switch (this.selectedCourt.id) {
            case CourtId.Indoor:
              this.timeSlots =
                this.timeSlotsResponse?.availableIndoorSlots?.bookingSlots ??
                [];
              this.minimumPlayingTime =
                this.timeSlotsResponse?.availableIndoorSlots
                  ?.minimumPlayingTime ?? 0;
              break;
            case CourtId.Outdoor:
              this.timeSlots =
                this.timeSlotsResponse?.availableOutdoorSlots?.bookingSlots ??
                [];
              this.minimumPlayingTime =
                this.timeSlotsResponse?.availableOutdoorSlots
                  ?.minimumPlayingTime ?? 0;
              break;
            case CourtId.Singles:
              this.timeSlots =
                this.timeSlotsResponse?.availableSingleSlots?.bookingSlots ??
                [];
              this.minimumPlayingTime =
                this.timeSlotsResponse?.availableSingleSlots
                  ?.minimumPlayingTime ?? 0;
              break;
            case CourtId.IndoorLegacy:
              this.timeSlots =
                this.timeSlotsResponse?.availableIndoorLegacySlots
                  ?.bookingSlots ?? [];
              this.minimumPlayingTime =
                this.timeSlotsResponse?.availableIndoorLegacySlots
                  ?.minimumPlayingTime ?? 0;
              break;
          }
        }
      }
    );
  }

  resetAllTimesSlotsValues = () => {
    this.timeSlotsResponse = null;
    this.timeSlots = [];
    this.selectedTimeSlots = [];
    this.isMinimumPlayingTimeCovered = false;
    this.minimumPlayingTime = 0;
  };

  setTotalPlayersInMatch = (totalPlayersInMatch: number) => {
    this.totalPlayersInMatch = totalPlayersInMatch;
  };

  setSelectedTimeSlot = (slot: SpecificBookingSlot) => {
    // If no slots are selected, add the first one
    var slots = [...this.selectedTimeSlots];
    if (this.selectedTimeSlots.length === 0) {
      slots.push(slot);
      this.selectedTimeSlots = slots;
      return;
    }

    const lastSelectedSlot = slots[slots.length - 1];
    const firstSelectedSlot = slots[0];

    if (this.isContinuousTimeSlot(lastSelectedSlot, firstSelectedSlot, slot)) {
      slots.push(slot);
    } else {
      // Reset selection if non-continuous slot is selected
      slots = [slot];
      // this.modalData = {
      //   visible: true,
      //   title: Strings.WARNING,
      //   children: "Please select continuous time slots",
      //   btnText: Strings.OK,
      //   btnVariant: "outline-danger",
      //   onClose: () => (this.modalData = undefined),
      // };
    }
    this.selectedTimeSlots = slots.sort(
      (a, b) => a.startSlotTime - b.startSlotTime
    );
  };

  isContinuousTimeSlot = (
    lastSelected: SpecificBookingSlot,
    nextSelected: SpecificBookingSlot,
    item: SpecificBookingSlot
  ) => {
    const comparisonBar = item.endSlotTime - item.startSlotTime;
    return (
      (Math.abs(lastSelected.endSlotTime - item.endSlotTime) ===
        comparisonBar &&
        Math.abs(lastSelected.startSlotTime - item.startSlotTime) ===
          comparisonBar) ||
      (Math.abs(nextSelected.endSlotTime - item.endSlotTime) ===
        comparisonBar &&
        Math.abs(nextSelected.startSlotTime - item.startSlotTime) ===
          comparisonBar)
    );
  };

  calculateTotalDuration = (items: SpecificBookingSlot[]) => {
    let totalDuration = 0;
    items.forEach((item) => {
      const duration = item.endSlotTime - item.startSlotTime;
      totalDuration += duration;
    });
    return totalDuration;
  };

  // Check if the minimum playing time is covered
  setIsMinimumPlayingTimeCovered = (
    items: SpecificBookingSlot[],
    minimumPlayingTime: number
  ) => {
    const totalDuration = this.calculateTotalDuration(items);
    this.isMinimumPlayingTimeCovered = totalDuration >= minimumPlayingTime;
  };

  clearSelectedTimeSlots = () => {
    this.selectedTimeSlots = [];
  };

  setSelectedDate(dateInfo: DateInfo) {
    this.selectedDate = dateInfo;
  }

  fetchTimeSlots = async () => {
    try {
      this.isLoading = true;
      const response = await CourtBookingApi.getTimeSlots(
        getDateInYYYYMMDDFormat(this.selectedDate?.fullDate ?? new Date()),
        false,
        ULTRA_SERVICE_LOCATION_ENUM.MAGIC_CITY
      );
      this.timeSlotsResponse = response;
    } catch (err) {
      console.log(err);
      this.modalData = {
        visible: true,
        title: Strings.WARNING,
        children: "Error occurred while fetching time slots",
        btnText: Strings.OK,
        btnVariant: "outline-danger",
        onClose: () => (this.modalData = undefined),
      };
      this.resetAllTimesSlotsValues();
    } finally {
      this.isLoading = false;
    }
  };

  setSelectedCourt(court: CourtType) {
    this.selectedCourt = court;
  }

  getCalenderMinDate = () => {
    const date = new Date();
    date.setTime(
      this.nextSixDays[
        REQUEST_PLAYER_DATES_LIST_COUNT - 1
      ]?.fullDate.getTime() || 0
    );
    date.setDate(date.getDate() + 1);
    return date;
  };
  // Generate the next six days from today
  generateNextSixDays = () => {
    this.nextSixDays = Array.from(
      { length: REQUEST_PLAYER_DATES_LIST_COUNT },
      (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() + i);

        return {
          date: date.getDate().toString(),
          day: days[date.getDay()],
          month: months[date.getMonth()],
          fullDate: new Date(date),
        };
      }
    );
  };

  // Example action to fetch courts
  fetchCourts = async () => {
    this.isLoading = true;
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));
    } catch (error) {
      console.error("Failed to fetch courts:", error);
    } finally {
      this.isLoading = false;
    }
  };

  invitePlayer = async (bookingId: number, navigate: NavigateFunction) => {
    const payload: InvitePlayersPayload = {
      bookingId: bookingId,
      selectedCircles: this.rootStore.selectCircleStore.selectedCircles,
      excludedPlayers: this.rootStore.selectCircleStore.excludedPlayers.length
        ? this.rootStore.selectCircleStore.excludedPlayers.flatMap(
            (p) => p.userId
          )
        : [],
      bookingDate: getDateInYYYYMMDDFormat(
        this.selectedDate?.fullDate ?? new Date()
      ),
      startSlotTime: this.selectedTimeSlots[0].startSlotTime,
      endSlotTime:
        this.selectedTimeSlots[this.selectedTimeSlots.length - 1].endSlotTime,
      lowerLimit: this.rootStore.userStore.getMinRating(),
      upperLimit: this.rootStore.userStore.getMaxRating(),
      gender: this.rootStore.userStore.userModel?.gender ?? 0, // TODO: add gender
    };
    try {
      await CourtBookingApi.sendInvitationToPlayers(payload);
      this.modalData = {
        visible: true,
        title: Strings.SUCCESS,
        children: `Court has been booked successfully with booking id: ${bookingId} and invitation has been sent to players`,
        btnText: Strings.OK,
        btnVariant: "outline-success",
        onClose: () => {
          this.modalData = undefined;
          navigate("/success", { replace: true });
        },
      };
    } catch (error) {
      this.modalData = {
        visible: true,
        title: Strings.WARNING,
        children: `Your court has been booked with booking id: ${bookingId} but some error occurred while sending invitation to players`,
        btnText: Strings.OK,
        btnVariant: "outline-danger",
        onClose: () => (this.modalData = undefined),
      };
    } finally {
      this.isLoading = false;
    }
  };

  onCourtBookingFailure = () => {
    this.isLoading = false;
    this.modalData = {
      visible: true,
      title: Strings.WARNING,
      children: "Error booking court",
      btnText: Strings.OK,
      btnVariant: "outline-danger",
      onClose: () => (this.modalData = undefined),
    };
  };

  onSuccessfulCourtBooking = (
    bookingId: number,
    navigate: NavigateFunction
  ) => {
    this.isLoading = false;
    // this.modalData = {
    //   visible: true,
    //   title: Strings.SUCCESS,
    //   children: `Court has been booked successfully with booking id: ${bookingId}`,
    //   btnText: Strings.OK,
    //   btnVariant: "outline-success",
    //   onClose: () => {
    //     this.modalData = undefined;
    //     navigate("/success", { replace: true });
    //   },
    // };
    navigate("/success", { replace: true });
  };

  bookCourt = async (navigate: NavigateFunction) => {
    this.isLoading = true;
    const date = getDateInYYYYMMDDFormat(
      this.selectedDate?.fullDate ?? new Date()
    );
    const startSlotTime = this.selectedTimeSlots[0].startSlotTime;
    const endSlotTime =
      this.selectedTimeSlots[this.selectedTimeSlots.length - 1].endSlotTime;
    const isRematch = false;
    const totalAmount = this.rootStore.payemntStore.totalAmount;
    const selectedCourt = this.selectedCourt.id;
    const selectedCircles = this.rootStore.selectCircleStore.selectedCircles;
    const paymentId =
      this.rootStore.payemntStore.selectedPaymentMethod ===
      BookingPaymentType.WALLET
        ? -1
        : this.rootStore.payemntStore.defaultPaymentCard?.id;

    // const bookingService =
    //   this.rootStore.payemntStore.productServicesResponse.find(
    //     (v) => v.categoryId === BOOKING_FEES_SERVICE_ID
    //   );

    const isPaymentThroughExternalWallet =
      this.rootStore.payemntStore.selectedPaymentMethod ===
      BookingPaymentType.EXTERNAL_WALLET;

    const players = this.rootStore.selectCircleStore
      .adjustAndGetSelectedPlayersList()
      .map((pl) => {
        const courtBookingPayment: CourtBookingPayment = {
          playerId: pl.userId,
          includePayment: this.rootStore.payemntStore.selectedPlayersToPay.some(
            (v) => v.userId.toString() === pl.userId.toString()
          ),
          products: [
            {
              productId:
                this.rootStore.payemntStore.playerPricesMap.get(pl.userId)
                  ?.id ?? 0,
              amount: pl.totalAmount ?? 0,
              categId: 0,
              quantity: 1,
            },
          ],
          "ext-wallet": isPaymentThroughExternalWallet,
        };
        return courtBookingPayment;
      });

    const payload: CourtBookingPayload = {
      bookingDate: date ?? "",
      startSlotTime: startSlotTime,
      endSlotTime: endSlotTime,
      isRematch: isRematch,
      totalAmount: totalAmount,
      payments: players,
      courtType: selectedCourt,
      selectedCircles: selectedCircles,
      // paymentId: paymentId ?? -1,
      paymentId: 2,
    };
    try {
      const response = await CourtBookingApi.bookMatch(payload);
      if (response.isSuccess) {
        if (
          this.rootStore.selectCircleStore.selectedPlayers.length ===
          this.totalPlayersToSelect
        ) {
          this.onSuccessfulCourtBooking(response.data, navigate);
        } else {
          this.invitePlayer(response.data, navigate);
        }
      } else {
        this.onCourtBookingFailure();
      }
    } catch (error) {
      this.onCourtBookingFailure();
    } finally {
    }
  };

  bookCourtV2 = async (navigate: NavigateFunction) => {
    this.isLoading = true;
    const date = getDateInYYYYMMDDFormat(
      this.selectedDate?.fullDate ?? new Date()
    );
    const startSlotTime = this.selectedTimeSlots[0].startSlotTime;
    const endSlotTime =
      this.selectedTimeSlots[this.selectedTimeSlots.length - 1].endSlotTime;
    const isRematch = false;
    const totalAmount = this.rootStore.payemntStore.totalAmount;
    const selectedCourt = this.selectedCourt.id;
    const selectedCircles = this.rootStore.selectCircleStore.selectedCircles;
    const paymentId =
      this.rootStore.payemntStore.selectedPaymentMethod ===
      BookingPaymentType.WALLET
        ? -1
        : this.rootStore.payemntStore.defaultPaymentCard?.id;

    // const bookingService =
    //   this.rootStore.payemntStore.productServicesResponse.find(
    //     (v) => v.categoryId === BOOKING_FEES_SERVICE_ID
    //   );

    const isPaymentThroughExternalWallet =
      this.rootStore.payemntStore.selectedPaymentMethod ===
      BookingPaymentType.EXTERNAL_WALLET;

    const players = this.rootStore.selectCircleStore
      .adjustAndGetSelectedPlayersList()
      .map((pl) => {
        const courtBookingPayment: CourtBookingPayment = {
          playerId: pl.userId,
          includePayment: this.rootStore.payemntStore.selectedPlayersToPay.some(
            (v) => v.userId.toString() === pl.userId.toString()
          ),
          products: [
            {
              productId:
                this.rootStore.payemntStore.playerPricesMap.get(pl.userId)
                  ?.id ?? 0,
              amount: pl.totalAmount ?? 0,
              categId: 0,
              quantity: 1,
            },
          ],
          "ext-wallet": isPaymentThroughExternalWallet,
        };
        return courtBookingPayment;
      });

    const payload: CourtBookingPayload = {
      bookingDate: date ?? "",
      startSlotTime: startSlotTime,
      endSlotTime: endSlotTime,
      isRematch: isRematch,
      totalAmount: totalAmount,
      payments: players,
      courtType: selectedCourt,
      selectedCircles: selectedCircles,
      paymentId: paymentId ?? -1,
    };
    try {
      const response = await CourtBookingApi.bookMatchV2(payload);
      if (response.isSuccess) {
        if (
          this.rootStore.selectCircleStore.selectedPlayers.length ===
          this.totalPlayersToSelect
        ) {
          this.onSuccessfulCourtBooking(response.data, navigate);
        } else {
          this.invitePlayer(response.data, navigate);
        }
      } else {
        this.onCourtBookingFailure();
      }
    } catch (error) {
      this.onCourtBookingFailure();
    } finally {
    }
  };

  addCustomDate = (selectedDate: Date) => {
    const customDateInfo = {
      date: selectedDate.getDate().toString(),
      day: days[selectedDate.getDay()],
      month: months[selectedDate.getMonth()],
      fullDate: new Date(selectedDate),
    };

    // Check if this date already exists in the array
    const dateExists = this.nextSixDays.some(
      (dateInfo) =>
        dateInfo.fullDate.toDateString() ===
        customDateInfo.fullDate.toDateString()
    );

    if (!dateExists) {
      // Add the custom date and sort the array by date
      if (this.nextSixDays.length > REQUEST_PLAYER_DATES_LIST_COUNT) {
        this.nextSixDays[REQUEST_PLAYER_DATES_LIST_COUNT] = customDateInfo;
      } else {
        this.nextSixDays.push(customDateInfo);
      }
      this.nextSixDays.sort(
        (a, b) => a.fullDate.getTime() - b.fullDate.getTime()
      );
      this.setSelectedDate(customDateInfo);
    }
  };
}
